import { ClipboardX, RotateCcw } from "lucide-react";
import * as React from "react";

import { Badge, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";

const COUNT_BADGE_CLASS = "h-5 shrink-0 gap-1 px-1.5 py-0 text-[10px] font-medium shadow-none";

function countLabel(count: number, singular: string, plural: string) {
  return count === 1 ? `1 ${singular}` : `${count} ${plural}`;
}

function revisionTone(revisionCount: number) {
  if (revisionCount === 1) return "border-amber-200 bg-amber-50 text-amber-800";
  if (revisionCount === 2) return "border-orange-200 bg-orange-50 text-orange-800";
  return "border-red-200 bg-red-50 text-red-800";
}

function CountBadge({
  count,
  tooltip,
  toneClass,
  icon,
  className,
  onClick,
}: {
  count: number;
  tooltip: string;
  toneClass: string;
  icon: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  const badge = (
    <Badge variant="outline" className={cn(COUNT_BADGE_CLASS, toneClass)} aria-label={tooltip}>
      {icon}
      {count}
    </Badge>
  );

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          onClick ? (
            <button type="button" className={cn("inline-flex cursor-pointer", className)} onClick={onClick}>
              {badge}
            </button>
          ) : (
            <span className={cn("inline-flex", className)}>{badge}</span>
          )
        }
      />
      <Tooltip.Content>{tooltip}</Tooltip.Content>
    </Tooltip>
  );
}

interface VideoProjectRevisionBadgeProps {
  revisionCount?: number | null;
  className?: string;
}

export function VideoProjectRevisionBadge({ revisionCount, className }: VideoProjectRevisionBadgeProps) {
  const count = revisionCount ?? 0;
  if (count <= 0) return null;

  return (
    <CountBadge
      count={count}
      tooltip={countLabel(count, "revision", "revisions")}
      toneClass={revisionTone(count)}
      icon={<RotateCcw className="size-2.5" />}
      className={className}
    />
  );
}

interface VideoProjectQaBadgesProps {
  qaFailed?: boolean;
  qaFailureCount?: number | null;
  className?: string;
  onQaFailedClick?: () => void;
}

export function VideoProjectQaBadges({
  qaFailed = false,
  qaFailureCount,
  className,
  onQaFailedClick,
}: VideoProjectQaBadgesProps) {
  const count = qaFailureCount ?? 0;
  if (count <= 0) return null;

  return (
    <CountBadge
      count={count}
      tooltip={countLabel(count, "QA failure", "QA failures")}
      toneClass={qaFailed ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-800"}
      icon={<ClipboardX className="size-2.5" />}
      className={className}
      onClick={onQaFailedClick}
    />
  );
}
